import { Button } from "@/components/ui/button";
import { CalendarDays, Linkedin } from "lucide-react";
import { Link } from "react-router-dom";
import type { TeamMember } from "@/lib/teamData";

interface ExecutiveProfileCardProps {
  member: TeamMember;
  size?: "large" | "medium";
  priority?: boolean;
}

const ExecutiveProfileCard = ({
  member,
  size = "medium",
  priority = false,
}: ExecutiveProfileCardProps) => {
  const minHeight = size === "large" ? "min-h-[720px] sm:min-h-[820px]" : "min-h-[640px] sm:min-h-[720px]";

  return (
    <article
      className={`group relative overflow-hidden rounded-[26px] border border-[hsl(var(--line))] bg-gradient-to-b from-[#05111f] via-[#03060d] to-black ${minHeight}`}
      style={{
        boxShadow:
          "0 0 0 1px hsl(var(--line)), 0 0 36px hsl(var(--primary) / 0.16), 0 16px 58px rgba(0, 0, 0, 0.46)",
      }}
    >
      <Link
        to={`/team/${member.slug}`}
        aria-label={`View ${member.name} profile`}
        className="absolute inset-0 flex items-start justify-center px-2 pt-2 pb-24"
      >
        <img
          src={member.cardImage}
          alt={`${member.name} executive profile card`}
          className="h-full w-full object-contain object-top transition-transform duration-500 group-hover:scale-[1.015]"
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
        />
      </Link>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/85 to-transparent" />

      <div className="pointer-events-none absolute left-4 top-4 z-20 rounded-full border border-primary/40 bg-background/65 px-3 py-1 text-[11px] font-semibold tracking-[0.18em] text-primary backdrop-blur-sm">
        PROFILE
      </div>

      <div className="absolute inset-x-4 bottom-4 z-20 rounded-2xl border border-[hsl(var(--line))] bg-background/82 p-3 backdrop-blur-md">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            asChild
            className="h-9 rounded-full bg-primary px-4 text-xs text-primary-foreground shadow-[0_0_24px_hsl(var(--primary)/0.35)] hover:bg-primary/90"
          >
            <a href={member.contactUrl} target="_blank" rel="noopener noreferrer" aria-label={`Contact ${member.name}`}>
              <CalendarDays className="mr-1.5 h-3.5 w-3.5" />
              Contact
            </a>
          </Button>

          <Button
            asChild
            variant="outline"
            className="h-9 rounded-full border-accent/50 bg-background/40 px-4 text-xs text-accent hover:bg-accent/10 hover:text-accent"
          >
            <a href={member.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${member.name} LinkedIn`}>
              <Linkedin className="mr-1.5 h-3.5 w-3.5" />
              LinkedIn
            </a>
          </Button>

          <Link
            to={`/team/${member.slug}`}
            className="ml-auto text-xs font-semibold uppercase tracking-[0.12em] text-foreground/90 transition-colors hover:text-primary"
            aria-label={`Read full profile for ${member.name}`}
          >
            Full profile
          </Link>
        </div>
      </div>
    </article>
  );
};

export default ExecutiveProfileCard;
